import type { TFunction } from 'i18next';
import type { FactorDimension, HmsApproval } from '../../api/hms';

export const CONSEQUENCE_LEVELS = [1, 2, 3, 4, 5] as const;

export const FACTOR_DIMENSIONS: readonly FactorDimension[] = ['LOAD', 'RESOURCE'];

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** «3. mars 2026» */
export const fullDate = (value: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));

/** «juni 2026» */
export const monthAndYear = (value: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));

/** «3. mars 2026», or «i dag» for today */
export const formatDay = (value: string, t: TFunction, language: string) =>
  sameDay(new Date(value), new Date()) ? t('hms.today') : fullDate(value, language);

/** HR approves without a comment only when the organisation has no main safety representative */
export const approvedBy = (approval: HmsApproval, t: TFunction) => {
  if (approval.mode === 'SAFETY_REP') return t('hms.approvedBy.safetyRep');
  return approval.comment ? t('hms.approvedBy.hr', { comment: approval.comment }) : t('hms.approvedBy.hrAlone');
};

/** «a, b eller c» and «a, b og c» */
export const joinList = (items: string[], t: TFunction, kind: 'or' | 'and') =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} ${t(kind === 'or' ? 'hms.listOr' : 'hms.listAnd')} ${items[items.length - 1]}`;

/** «3 – moderat» */
export const consequenceLabel = (level: number, t: TFunction) =>
  t('hms.consequenceShort', { level, name: t(`hms.consequenceLevels.${level}`).toLowerCase() });

/** «3. Moderat», as in the select lists */
export const consequenceOption = (level: number, t: TFunction) =>
  t('hms.consequenceOption', { level, name: t(`hms.consequenceLevels.${level}`) });
